import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import UserCreateDrawer from '../components/UserCreateDrawer';
import UserDetailDrawer from '../components/drawers/UserDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { Search, Download, UserPlus, ChevronLeft, ChevronRight, Gem, Star, Filter } from 'lucide-react';
const Users = () => {
  const location = useLocation();
  const {
    users
  } = useAdminData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const usersPerPage = 10;
  useEffect(() => {
    if (location.state?.openDrawer) {
      setShowCreateDrawer(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  const uniqueCities = useMemo(() => {
    const cities = users.map(u => u.luogo).filter(Boolean);
    return ['all', ...Array.from(new Set(cities))];
  }, [users]);
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = user.nome.toLowerCase().includes(searchTerm.toLowerCase()) || user.cognome.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      const matchesCity = cityFilter === 'all' || user.luogo === cityFilter;
      return matchesSearch && matchesStatus && matchesCity;
    });
  }, [users, searchTerm, statusFilter, cityFilter]);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const handleExport = () => {
    console.log('Export CSV');
  };
  const handleAddUser = () => {
    setShowCreateDrawer(true);
  };
  const handleRowClick = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailDrawer(true);
  };
  const handleCloseDetailDrawer = () => {
    setShowDetailDrawer(false);
    setTimeout(() => setSelectedUserId(null), 300);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-4277">
      <div className="w-20 flex-shrink-0" data-id="element-4278">
        <Sidebar onAddUser={handleAddUser} data-id="element-4279" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-4280">
        <Topbar data-id="element-4281" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-4282">
          <PageHeader title="Utenti" subtitle="Gestisci tutti gli utenti della piattaforma Greenback" primaryAction={{
          label: 'Aggiungi Utente',
          onClick: handleAddUser,
          icon: <UserPlus className="h-4 w-4 mr-2" data-id="element-4284" />
        }} secondaryAction={{
          label: 'Esporta CSV',
          onClick: handleExport,
          icon: <Download className="h-4 w-4 mr-2" data-id="element-4285" />
        }} data-id="element-4283" />

          {/* Filters - Updated to rounded-sm */}
          <div className="mb-6 flex flex-wrap gap-3" data-id="element-4286">
            <div className="relative flex-1 min-w-[300px]" data-id="element-4287">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-4288" />
              <input type="text" placeholder="Cerca per nome, cognome o email..." className="w-full rounded-sm border border-gray-300 pl-10 pr-4 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-green-600" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} data-id="element-4289" />
            </div>

            <div className="flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-4290">
              <Filter className="h-4 w-4 text-gray-500" data-id="element-4291" />
              <select className="bg-transparent text-sm font-normal focus:outline-none text-gray-700" value={statusFilter} onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }} data-id="element-4292">
                <option value="all" data-id="element-4293">Tutti gli stati</option>
                <option value="active" data-id="element-4294">Attivo</option>
                <option value="inactive" data-id="element-4295">Inattivo</option>
                <option value="suspended" data-id="element-4296">Sospeso</option>
              </select>
            </div>

            <div className="flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-4297">
              <Filter className="h-4 w-4 text-gray-500" data-id="element-4298" />
              <select className="bg-transparent text-sm font-normal focus:outline-none text-gray-700" value={cityFilter} onChange={e => {
              setCityFilter(e.target.value);
              setCurrentPage(1);
            }} data-id="element-4299">
                <option value="all" data-id="element-4300">Tutte le città</option>
                {uniqueCities.filter(city => city !== 'all').map(city => <option key={city} value={city} data-id="element-4301">
                      {city}
                    </option>)}
              </select>
            </div>
          </div>

          {/* Users Table - Updated rounded-sm */}
          <div className="rounded-sm border border-gray-200 bg-white overflow-hidden" data-id="element-4302">
            <div className="overflow-x-auto" data-id="element-4303">
              <table className="w-full" data-id="element-4304">
                <thead data-id="element-4305">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4306">
                    <th className="px-6 py-3" data-id="element-4307">Utente</th>
                    <th className="px-6 py-3" data-id="element-4308">Email</th>
                    <th className="px-6 py-3" data-id="element-4309">Luogo</th>
                    <th className="px-6 py-3" data-id="element-4310">Obiettivi</th>
                    <th className="px-6 py-3" data-id="element-4311">
                      <div className="flex items-center gap-1" data-id="element-4312">
                        <Gem className="h-3 w-3" data-id="element-4313" />
                        Ottenuti
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4314">
                      <div className="flex items-center gap-1" data-id="element-4315">
                        <Gem className="h-3 w-3" data-id="element-4316" />
                        Spesi
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4317">
                      <div className="flex items-center gap-1" data-id="element-4318">
                        <Star className="h-3 w-3" data-id="element-4319" />
                        Ottenuti
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4320">
                      <div className="flex items-center gap-1" data-id="element-4321">
                        <Star className="h-3 w-3" data-id="element-4322" />
                        Spesi
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-4323">Riscatti</th>
                    <th className="px-6 py-3 text-right" data-id="element-4324">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-4325">
                  {currentUsers.map(user => <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(user.id)} data-id="element-4326">
                      <td className="px-6 py-4" data-id="element-4327">
                        <div className="flex items-center" data-id="element-4328">
                          <img src={user.avatar} alt={`${user.nome} ${user.cognome}`} className="h-10 w-10 rounded-full object-cover mr-3" data-id="element-4329" />
                          <div data-id="element-4330">
                            <div className="font-medium text-gray-900" data-id="element-4331">
                              {user.nome} {user.cognome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-4332">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-4333">
                        {user.luogo}
                      </td>
                      <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-4334">
                        {user.obiettivi}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-blue-600" data-id="element-4335">
                        {user.creditiDiamanteOttenuti.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-4336">
                        {user.creditiDiamanteSpesi.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-amber-600" data-id="element-4337">
                        {user.creditiStellaOttenuti.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-4338">
                        {user.creditiStellaSpesi.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-4339">
                        {user.claimFatti}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-4340">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleRowClick(user.id);
                    }} className="text-green-600 hover:text-green-700 text-sm font-medium" data-id="element-4341">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination - Updated rounded-sm */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-4342">
              <div className="text-sm font-normal text-gray-500" data-id="element-4343">
                Mostrando {indexOfFirstUser + 1} -{' '}
                {Math.min(indexOfLastUser, filteredUsers.length)} di{' '}
                {filteredUsers.length} utenti
              </div>
              <div className="flex items-center gap-2" data-id="element-4344">
                <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4345">
                  <ChevronLeft className="h-4 w-4" data-id="element-4346" />
                </button>
                <span className="text-sm font-normal text-gray-700" data-id="element-4347">
                  Pagina {currentPage} di {totalPages}
                </span>
                <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4348">
                  <ChevronRight className="h-4 w-4" data-id="element-4349" />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* User Create Drawer */}
      <UserCreateDrawer isOpen={showCreateDrawer} onClose={() => setShowCreateDrawer(false)} onSuccess={() => {
      console.log('User created successfully');
    }} data-id="element-4350" />

      {/* User Detail Drawer */}
      <UserDetailDrawer isOpen={showDetailDrawer} onClose={handleCloseDetailDrawer} userId={selectedUserId} data-id="element-4351" />
    </div>;
};
export default Users;